import { useEscapeKey } from '@/components/shortcuts';

/**
 * The booking screens' small overlay — the phone's sheet, on the web: a line of the bag's Change,
 * hold or quote, a stage move that asks something first.
 *
 * <p>
 * Shaped like `GarmentDiscountModal` (the overlay tokens, the shortcut layer, Esc closes), so every
 * panel the booking screens open looks like one family. It mounts where the page content does, which
 * is what keeps it above the sidebar (CLAUDE.md, "Overlays and z-index").
 * </p>
 */
export function Dialog({
  title,
  subtitle,
  onClose,
  footer,
  width = 440,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
  width?: number;
  children: React.ReactNode;
}) {
  useEscapeKey(onClose);

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 print:hidden"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <div
        role="dialog"
        aria-label={title}
        style={{ maxWidth: width }}
        className="border-[var(--overlay-border)] relative flex max-h-[90vh] w-full flex-col rounded-md border bg-[var(--overlay-surface)] shadow-lg"
      >
        <header className="border-border-color flex items-start gap-3 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            {subtitle && <p className="text-default text-[12px]">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-default flex size-7 cursor-pointer items-center justify-center rounded hover:bg-light"
          >
            <i className="icon icon-x" />
          </button>
        </header>

        <div className="flex flex-col gap-3 overflow-y-auto px-4 py-3">{children}</div>

        {footer && (
          <footer className="border-border-color flex items-center gap-2 border-t px-4 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

/**
 * One of a row of mutually exclusive answers — a pill that fills when chosen. `aria-pressed` carries
 * the state a screen reader cannot see. Shared by every panel of the new booking.
 */
export function Choice({
  label,
  caption,
  chosen,
  disabled = false,
  onChoose,
}: {
  label: string;
  caption?: string | null | undefined;
  chosen: boolean;
  disabled?: boolean;
  onChoose: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={chosen}
      onClick={onChoose}
      className={`flex cursor-pointer flex-col items-center justify-center rounded border px-3 py-1.5 text-[12.5px] font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
        chosen
          ? 'border-primary bg-primary text-white'
          : 'border-border-color bg-white text-gray-900 hover:bg-light'
      }`}
    >
      {label}
      {caption && (
        <span className={`text-[11px] font-normal ${chosen ? 'text-white/80' : 'text-default'}`}>
          {caption}
        </span>
      )}
    </button>
  );
}

/**
 * A heading over a row of `Choice`s. Not `Field`: that is a `<label>`, and a click on a label's
 * words presses the first button inside it.
 */
export function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="group" aria-label={label}>
      <p className="mb-1 text-sm font-semibold text-gray-900">{label}</p>
      {children}
    </div>
  );
}
